import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { AuthService } from "../../../../api/auth.service";
import { authStore } from "../../../../store/auth.store";
import { trackEvent } from "../../../../utils/analytics";
import { Card, CardHeader, CardTitle, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Alert } from "../../../ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../ui/alert-dialog";

const DeleteAccount = () => {
  const { formatMessage } = useIntl();

  const logOut = authStore(state => state.logOut);

  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const confirmWord = formatMessage({ id: 'DELETE_ACCOUNT_CONFIRM_WORD' });
  const confirmed = confirmation.trim().toLowerCase() === confirmWord.toLowerCase();

  const openDialog = () => {
    setConfirmation("");
    setError(null);
    setOpen(true);
  };

  const deleteAccount = async () => {
    if (!confirmed || deleting) return;
    setDeleting(true);
    try {
      await AuthService.deleteAccount();
      trackEvent('delete_account');
      // the account is gone — logOut clears the token, local storage and sends us to the login page
      logOut();
    } catch {
      setError(formatMessage({ id: 'DELETE_ACCOUNT_ERROR' }));
      setDeleting(false);
      setOpen(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{formatMessage({ id: 'DELETE_ACCOUNT' })}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {formatMessage({ id: 'DELETE_ACCOUNT_DESCRIPTION' })}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Alert variant="error" message={error} />}

        <Button variant="destructive" onClick={openDialog}>
          {formatMessage({ id: 'DELETE_ACCOUNT' })}
        </Button>

        <AlertDialog open={open} onOpenChange={value => !deleting && setOpen(value)}>
          <AlertDialogContent
            onOpenAutoFocus={event => {
              // focus the confirmation field instead of the Cancel button Radix picks by default
              event.preventDefault();
              inputRef.current?.focus();
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{formatMessage({ id: 'DELETE_ACCOUNT_TITLE' })}</AlertDialogTitle>
              <AlertDialogDescription>
                {formatMessage({ id: 'DELETE_ACCOUNT_WARNING' })}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="delete-account-confirmation">
                {formatMessage({ id: 'DELETE_ACCOUNT_CONFIRM_PROMPT' }, { word: confirmWord })}
              </label>
              <Input
                id="delete-account-confirmation"
                ref={inputRef}
                value={confirmation}
                placeholder={confirmWord}
                autoComplete="off"
                onChange={e => setConfirmation(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && deleteAccount()}
              />
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>
                {formatMessage({ id: 'CANCEL' })}
              </AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={!confirmed}
                loading={deleting}
                onClick={deleteAccount}
              >
                {formatMessage({ id: 'DELETE_ACCOUNT' })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default DeleteAccount;
